import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from "stylis";
import { create } from 'jss';
import rtl from 'jss-rtl';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { StylesProvider, jssPreset } from '@mui/styles';
import { getCookie, setCookie } from "../../../helpers/utils";

//theme
import { appTheme, themes } from "./index";

//context
import { ThemeContext } from "./context";

//constants
import { cookieTheme } from '../../../constants';
import { RTL_LANGUAGES } from '../../../constants/languages';

interface IMuiThemeProviderProps {
  children: React.ReactNode,
}

const MuiThemeProvider: React.FC<IMuiThemeProviderProps> = ({
  children,
}): JSX.Element => {
  const { i18n } = useTranslation();
  const isRTL = RTL_LANGUAGES.includes(i18n.language);
  
  const themePrefix = appTheme.split('_')[0];

  const [selectedThemeName, setSelectedThemeName] = useState(
    getCookie(cookieTheme).includes(`${themePrefix}`)
      ? (getCookie(cookieTheme) || appTheme)
      : appTheme
  );
  const [selectedTheme, setSelectedTheme] = useState(
    !!themes[selectedThemeName] ? themes[selectedThemeName] : themes[`${appTheme}`]
  );
  const [mode, setMode] = useState(
    (getCookie(cookieTheme).includes(`${themePrefix}`)
      ? (getCookie(cookieTheme) || `${themePrefix}_light`)
      : appTheme).includes('dark')
  );

  const cacheRtl = useMemo(() => createCache({
    key: isRTL ? 'muirtl' : 'muiltr',
    stylisPlugins: isRTL ? [prefixer, rtlPlugin] : [],
    prepend: true,
  }), [isRTL]);

  const jss = useMemo(() => {
    const plugins = [...jssPreset().plugins];
    if (isRTL) {
      plugins.push(rtl());
    }
    return create({ plugins });
  }, [isRTL]);

  useEffect(() => {
    document.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
  }, [isRTL]);

  const handleChangeTheme = (value: boolean) => {
    const newThemeName = value ? `${themePrefix}_dark` : `${themePrefix}_light`;

    const expiresDate = new Date();
    expiresDate.setDate(expiresDate.getDate() + 365);

    setSelectedThemeName(newThemeName);
    setSelectedTheme(themes[newThemeName]);
    setMode(value);
    setCookie(cookieTheme, newThemeName, {
      expires: expiresDate,
      secure: true,
    })
  }

  const directionTheme = createTheme({
    ...selectedTheme,
    direction: isRTL ? 'rtl' : 'ltr',
  });

  return (
    <CacheProvider value={cacheRtl}>
      <StylesProvider jss={jss}>
        <ThemeContext.Provider value={{
          darkMode: mode,
          setDarkMode: handleChangeTheme,
        }}>
          <ThemeProvider theme={directionTheme}>
            {children}
          </ThemeProvider>
        </ThemeContext.Provider>
      </StylesProvider>
    </CacheProvider>
  );
};

export default MuiThemeProvider;